import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Please use the link from your email.");
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    if (next.length < 8 || !/[a-zA-Z]/.test(next) || !/[^a-zA-Z]/.test(next)) {
      toast.error("Password must be 8+ characters with at least one letter and one number or symbol.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A192F] via-[#0F2238] to-[#0A192F] p-6">
      <div className="w-full max-w-md">
        <h1 className="font-display text-3xl text-white tracking-tight text-center">Reset your password</h1>
        <p className="text-sm text-slate-400 mt-2 mb-6 text-center">Choose a strong, unique password.</p>
        <div className="surface-card p-7" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center text-sm text-slate-700" data-testid="reset-password-missing-token">
              <AlertCircle className="h-10 w-10 mx-auto" style={{ color: "#EF4444" }} />
              <p className="mt-3">This page requires a reset token. Please use the link your administrator sent you.</p>
              <Link to="/login" className="text-xs text-teal-700 hover:underline mt-4 inline-block">Back to sign in</Link>
            </div>
          ) : done ? (
            <div className="text-center" data-testid="reset-password-done">
              <CheckCircle2 className="h-10 w-10 mx-auto" style={{ color: "#22C55E" }} />
              <h2 className="font-display text-xl text-slate-900 mt-3">Password reset</h2>
              <p className="text-sm text-slate-600 mt-2">Redirecting you to sign in…</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <Label htmlFor="next">New password</Label>
                <Input
                  id="next" type="password" autoComplete="new-password" autoFocus required minLength={8}
                  value={next} onChange={(e) => setNext(e.target.value)}
                  data-testid="reset-password-new"
                  className="h-11 rounded-xl mt-1.5"
                />
                <p className="text-[11px] text-slate-500 mt-1.5">At least 8 characters, with one letter and one number or symbol.</p>
              </div>
              <div>
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm" type="password" autoComplete="new-password" required minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm"
                  className="h-11 rounded-xl mt-1.5"
                />
              </div>
              <Button type="submit" disabled={submitting} className="ai-cta rounded-xl px-5 h-11 w-full" data-testid="reset-password-submit">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
                Reset password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
